import { supabase } from '../lib/supabase';
import { documentService } from './documentService';
import { incidentService } from './incidentService';
import { snagService } from './snagService';
import { dailyTaskService } from './taskService';
import { siteDiaryService } from './siteDiaryService';
import { nearMissService } from './nearMissService';
import { ramsService } from './ramsService';
import { taskPhotoService } from './taskPhotoService';

export const reportService = {
  /**
   * Fetch all data for a compliance report.
   * @param {object} options - { siteId, companyId, dateFrom, dateTo, modules }
   *
   * companyId is required for diary and rams fetchers, which scope by both
   * site_id and company_id. Other fetchers are RLS-scoped to the caller's
   * company so siteId alone is sufficient for them.
   */
  async fetchReportData({ siteId, companyId, dateFrom, dateTo, modules = [] }) {
    try {
      const filterByDate = (items, dateField = 'created_at') => {
        if (!items || !Array.isArray(items)) return [];
        return items?.filter(item => {
          const raw = item?.[dateField] || item?.created_at || item?.incident_date || item?.task_date;
          if (!raw) return true;
          const d = new Date(raw);
          if (dateFrom && d < new Date(dateFrom)) return false;
          if (dateTo) {
            const toEnd = new Date(dateTo);
            toEnd?.setHours(23, 59, 59, 999);
            if (d > toEnd) return false;
          }
          return true;
        });
      };

      const promises = [
        // 0: site details
        siteId
          ? supabase?.from('sites')?.select('*, user_profiles!sites_created_by_fkey(full_name)')?.eq('id', siteId)?.single()
          : Promise.resolve({ data: null }),

        // 1: documents
        (modules?.includes('documents') || modules?.includes('certificates'))
          ? (siteId ? documentService?.getDocumentsBySite(siteId) : documentService?.getAllDocuments())
          : Promise.resolve({ data: [] }),

        // 2: incidents
        modules?.includes('incidents')
          ? (siteId ? incidentService?.getIncidentsBySite(siteId) : incidentService?.getAllIncidents())
          : Promise.resolve({ data: [] }),

        // 3: snags
        modules?.includes('snags')
          ? (siteId ? snagService?.getSnagsBySite(siteId) : snagService?.getAllSnags())
          : Promise.resolve({ data: [] }),

        // 4: tasks (completed)
        modules?.includes('tasks')
          ? (siteId && dateFrom && dateTo
              ? dailyTaskService?.getForDateRange(dateFrom, dateTo, siteId)
              : dailyTaskService?.getForDateRange(
                  dateFrom || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
                  dateTo || new Date()?.toISOString()?.split('T')?.[0],
                  siteId || null
                )
            )
          : Promise.resolve({ data: [] }),

        // 5: diary (daily_site_records). Requires both siteId and companyId
        // — the service double-filters because RLS alone doesn't pick a site.
        modules?.includes('diary') && siteId && companyId
          ? siteDiaryService?.getEntriesForSite(siteId, companyId)
          : Promise.resolve({ data: [] }),

        // 6: near misses
        modules?.includes('near_misses') && siteId
          ? nearMissService?.getNearMissesForSite(siteId)
          : Promise.resolve({ data: [] }),

        // 7: RAMS docs (acknowledgement + assignment counts fetched in a
        // separate batch below to avoid an N+1 over the doc list). At
        // "All Sites" scope (no siteId) fall back to the company-wide
        // fetch so the H&S register isn't silently empty.
        modules?.includes('rams') && companyId
          ? (siteId
              ? ramsService?.getRamsForSite(siteId, companyId)
              : ramsService?.getRamsForCompany(companyId))
          : Promise.resolve({ data: [] }),

        // 8: site-scoped task_photos (the rich photo source). Inline photo
        // extraction from snags/incidents continues below for source coverage.
        modules?.includes('photos') && siteId
          ? taskPhotoService?.getPhotosBySite(siteId)
          : Promise.resolve({ data: [] }),
      ];

      const [
        siteResult, docsResult, incidentsResult, snagsResult, tasksResult,
        diaryResult, nearMissResult, ramsResult, taskPhotosResult,
      ] = await Promise.all(promises);

      const allDocs = docsResult?.data || [];
      const allIncidents = incidentsResult?.data || [];
      const allSnags = snagsResult?.data || [];
      const allTasks = tasksResult?.data || [];
      const allDiary = diaryResult?.data || [];
      const allNearMisses = nearMissResult?.data || [];
      let allRams = ramsResult?.data || [];
      const taskPhotoRecords = taskPhotosResult?.data || [];

      // RAMS ack/assignment counts — two batch queries, no N+1. "Acknowledged:
      // X/Y" where X = ack rows for this doc, Y = assignment rows for this doc.
      if (modules?.includes('rams') && allRams?.length > 0) {
        try {
          const ramsIds = allRams?.map(r => r?.id)?.filter(Boolean);
          const [{ data: ackRows }, { data: assignRows }] = await Promise.all([
            supabase?.from('rams_acknowledgements')?.select('rams_document_id')?.in('rams_document_id', ramsIds),
            supabase?.from('rams_assignments')?.select('rams_document_id')?.in('rams_document_id', ramsIds),
          ]);
          const ackCount = {};
          const assignCount = {};
          (ackRows || [])?.forEach(a => { ackCount[a?.rams_document_id] = (ackCount?.[a?.rams_document_id] || 0) + 1; });
          (assignRows || [])?.forEach(a => { assignCount[a?.rams_document_id] = (assignCount?.[a?.rams_document_id] || 0) + 1; });
          allRams = allRams?.map(r => ({
            ...r,
            ack_count: ackCount?.[r?.id] || 0,
            assign_count: assignCount?.[r?.id] || 0,
          }));
        } catch (ramsCountErr) {
          // Non-fatal — render with zeros and let the auditor see the doc list.
          console.warn('[reportService] RAMS ack/assign count fetch failed:', ramsCountErr?.message);
        }
      }

      // Union H&S Docs uploaded into the generic documents table
      // (category='rams') — the Site Detail Documents tab and
      // /document-management write there, not to rams_documents. Without
      // this merge those uploads never reach the export's H&S Docs
      // section. Mirrors the GIVolooT merge on the Site Detail tab; the
      // durable schema consolidation belongs to 5m1glArl. rams_documents
      // and documents are distinct tables so there is no double-count.
      if (modules?.includes('rams') && companyId) {
        try {
          const docRamsResult = siteId
            ? await documentService?.getDocumentsBySite(siteId)
            : await documentService?.getAllDocuments();
          const docRamsRows = (docRamsResult?.data || [])
            ?.filter(d => (d?.category || '')?.toLowerCase() === 'rams')
            ?.map(d => ({
              id: d?.id,
              title: d?.title,
              file_name: d?.file_name,
              created_at: d?.created_at,
              uploader: null,
              ack_count: 0,
              assign_count: 0,
              source: 'documents',
            }));
          if (docRamsRows?.length) {
            allRams = [...allRams, ...docRamsRows];
          }
        } catch (docRamsErr) {
          // Non-fatal — rams_documents rows still render.
          console.warn('[reportService] documents-table H&S Docs merge failed:', docRamsErr?.message);
        }
      }

      const certificates = allDocs?.filter(d =>
        d?.category?.toLowerCase()?.includes('cert') ||
        d?.category?.toLowerCase()?.includes('qualification') ||
        d?.category?.toLowerCase()?.includes('licence') ||
        d?.category?.toLowerCase()?.includes('license')
      );

      // Collect photos from snags and incidents
      const photos = [];
      allSnags?.forEach(snag => {
        if (snag?.photo_paths?.length) {
          snag?.photo_paths?.forEach(path => {
            photos?.push({
              id: `snag-${snag?.id}-${path}`,
              path,
              source: 'snag',
              sourceTitle: snag?.title || 'Snag',
              date: snag?.created_at,
              site_name: snag?.sites?.site_name || '',
            });
          });
        }
      });
      allIncidents?.forEach(inc => {
        if (inc?.file_paths?.length) {
          inc?.file_paths?.forEach(path => {
            if (/\.(jpg|jpeg|png|gif|webp)$/i?.test(path)) {
              photos?.push({
                id: `inc-${inc?.id}-${path}`,
                path,
                source: 'incident',
                sourceTitle: inc?.title || inc?.description || 'Incident',
                date: inc?.incident_date || inc?.created_at,
                site_name: inc?.sites?.site_name || '',
              });
            }
          });
        }
      });
      allTasks?.forEach(task => {
        if (task?.photo_url) {
          photos?.push({
            id: `task-${task?.id}`,
            path: task?.photo_url,
            source: 'task',
            sourceTitle: task?.title || 'Task',
            date: task?.completed_at || task?.date,
            site_name: task?.site?.site_name || '',
          });
        }
      });
      // task_photos table — richer photo source with caption, uploader, and
      // client_visible flag. Merged into the same `photos` array under a
      // unified shape so the renderer doesn't branch per source.
      taskPhotoRecords?.forEach(p => {
        photos?.push({
          id: `taskphoto-${p?.id}`,
          path: p?.file_path,
          source: 'task',
          sourceTitle: p?.caption || p?.file_name || p?.task?.title || 'Task Photo',
          date: p?.created_at,
          site_name: p?.site?.site_name || '',
          uploaded_by_name: p?.uploaded_by_name || p?.uploader?.full_name || null,
          client_visible: p?.client_visible ?? null,
        });
      });

      return {
        data: {
          site: siteResult?.data || null,
          // category='rams' rows render in the H&S Docs section instead —
          // exclude them here so they aren't listed twice when both modules
          // are selected (5m1glArl owns the durable table consolidation).
          documents: filterByDate(allDocs)?.filter(d => (d?.category || '')?.toLowerCase() !== 'rams'),
          incidents: filterByDate(allIncidents, 'incident_date'),
          snags: filterByDate(allSnags),
          tasks: filterByDate(allTasks, 'task_date'),
          certificates: filterByDate(certificates),
          photos: filterByDate(photos, 'date'),
          diary: filterByDate(allDiary, 'record_date'),
          near_misses: filterByDate(allNearMisses, 'date'),
          // RAMS is not date-filtered — auditors expect the full applicable
          // RAMS register for the site regardless of report window.
          rams: allRams,
        },
        error: null,
      };
    } catch (err) {
      return { data: null, error: { message: err?.message } };
    }
  },
};
